import { cn } from "@design-system/utils";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import type { ReactMaskOpts } from "react-imask";
import { useIMask } from "react-imask";

import { inputVariants } from "./input-variants";

type InputVariant = NonNullable<VariantProps<typeof inputVariants>["variant"]>;

export interface InputProps extends React.ComponentProps<"input"> {
  debounce?: number;
  maskProps?: ReactMaskOpts;
  variant?: InputVariant;
}

function Input({
  className,
  type,
  maskProps,
  variant = "default",
  onChange,
  value,
  readOnly,
  ...props
}: InputProps) {
  const maskOptions = React.useMemo(() => {
    return maskProps || {};
  }, [maskProps]);

  const { ref } = useIMask<HTMLInputElement>(
    maskOptions,
    maskProps
      ? {
          onAccept: (value, maskRef) => {
            const valueFormatted: string =
              maskProps?.unmask === "typed"
                ? (maskRef.typedValue as string)
                : maskProps.unmask
                  ? maskRef.unmaskedValue
                  : value;
            const syntheticEvent = {
              target: { value: valueFormatted },
              currentTarget: { value: valueFormatted },
            } as React.ChangeEvent<HTMLInputElement>;

            onChange?.(syntheticEvent);
          },
        }
      : {}
  );

  return (
    <input
      ref={maskProps ? ref : undefined}
      type={type}
      data-slot="input"
      onChange={maskProps ? undefined : onChange}
      value={maskProps ? undefined : value}
      readOnly={readOnly}
      className={cn(
        inputVariants({ variant, readOnly: type !== "file" && !!readOnly }),
        className
      )}
      {...props}
    />
  );
}

export { Input };
